import { apiAxios } from '../utils/axios-instance/api-axios-instance';

export type ManagedProjectRole = 'ADMIN' | 'CONTRIBUTOR' | 'VIEWER';

export type OutputRequirementType = 'excel' | 'document' | 'dashboard';
export type OutputRequirementStatus = 'not_started' | 'in_progress' | 'done';

export interface OutputRequirement {
  id: string;
  title: string;
  description: string;
  type: OutputRequirementType;
  status: OutputRequirementStatus;
  outputFileId?: string;
  outputFileName?: string;
  outputDashboardId?: string;
  outputUrl?: string;
}

export interface ManagedProjectMember {
  id: string;
  createdAt: string;
  userId: string;
  userName?: string;
  role: ManagedProjectRole;
}

export interface ManagedProject {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  ownerId: string;
  createdByName?: string;
  ownerName?: string;
  name: string;
  slug: string;
  subtitle?: string;
  description?: string;
  objective?: string;
  outputRequirements?: OutputRequirement[];
  templateKey?: string;
  templateName?: string;
  icon?: string;
  color?: string;
  computeProfile?: string;
  coreOntologyDb?: string;
  useCaseOntologyDb?: string;
  teamId?: string;
  teamName?: string;
  workflowId?: string;
  workflowName?: string;
  isPublic: boolean;
  isActive: boolean;
  /**
   * Project-level override for the org-wide style guide. Free-form
   * markdown describing tone, layout opinions, palette preferences, and
   * any other rules agent-authored deliverables (interactive HTML apps,
   * MDX reports, decks) must follow. Empty string / null means "fall
   * back to the organization's style guide".
   */
  styleGuide?: string | null;
  isOwner?: boolean;
  canAccess?: boolean;
  memberRole?: ManagedProjectRole;
  memberCount: number;
  members?: ManagedProjectMember[];
}

export interface CreateManagedProjectInput {
  name: string;
  slug: string;
  subtitle?: string;
  description?: string;
  objective?: string;
  outputRequirements?: OutputRequirement[];
  templateKey?: string;
  templateName?: string;
  isPublic?: boolean;
}

export interface UpdateManagedProjectInput {
  name?: string;
  subtitle?: string;
  description?: string;
  objective?: string;
  outputRequirements?: OutputRequirement[];
  templateKey?: string;
  templateName?: string;
  isPublic?: boolean;
  isActive?: boolean;
  /**
   * Markdown style guide override. Set to an empty string or `null` to
   * clear the override and fall back to the organization-level guide.
   */
  styleGuide?: string | null;
}

export interface AddProjectMemberInput {
  userId: string;
  role: ManagedProjectRole;
}

export interface UpdateProjectMemberInput {
  role?: ManagedProjectRole;
}

export async function listManagedProjects(params?: {
  isActive?: boolean;
  mineOnly?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: ManagedProject[]; count: number }> {
  const response = await apiAxios.get('/managed-projects', {
    params: {
      limit: params?.limit ?? 100,
      offset: params?.offset ?? 0,
      ...(params?.isActive !== undefined && { isActive: params.isActive }),
      ...(params?.mineOnly !== undefined && { mineOnly: params.mineOnly }),
      ...(params?.search && { search: params.search }),
    },
  });
  return response.data;
}

export async function getManagedProject(id: string): Promise<ManagedProject> {
  const response = await apiAxios.get(`/managed-projects/${id}`);
  return response.data;
}

export async function createManagedProject(data: CreateManagedProjectInput): Promise<ManagedProject> {
  const response = await apiAxios.post('/managed-projects', data);
  return response.data;
}

export async function updateManagedProject(id: string, data: UpdateManagedProjectInput): Promise<ManagedProject> {
  const response = await apiAxios.patch(`/managed-projects/${id}`, data);
  return response.data;
}

export async function deleteManagedProject(id: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${id}`);
}

export async function addProjectMember(projectId: string, data: AddProjectMemberInput): Promise<ManagedProjectMember> {
  const response = await apiAxios.post(`/managed-projects/${projectId}/members`, data);
  return response.data;
}

export async function updateProjectMember(
  projectId: string,
  memberId: string,
  data: UpdateProjectMemberInput,
): Promise<ManagedProjectMember> {
  const response = await apiAxios.patch(`/managed-projects/${projectId}/members/${memberId}`, data);
  return response.data;
}

export async function removeProjectMember(projectId: string, memberId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/members/${memberId}`);
}

// ── Deliverable Comments ──────────────────────────────────────────
//
// Two endpoint surfaces back the same `ProjectComment` table:
//
//   1. Legacy `deliverables/:id/comments` — predates the polymorphic
//      schema; still used by the agent context processor and any older
//      client code. Returns rows that already have `subjectType=DELIVERABLE`
//      under the polymorphic schema, so the data is identical.
//   2. Polymorphic `/project-comments` + `/comments/:subjectType/:subjectId`
//      — supports FILE / ARTIFACT / DELIVERABLE subjects so we can let
//      users comment on any file in the workspace, not just registered
//      deliverables.
//
// New code should prefer the polymorphic helpers below
// (`listSubjectComments` / `createSubjectComment` / etc.). The legacy
// `*DeliverableComment*` helpers are kept as compatibility shims so we
// don't have to migrate every caller in one go.

/**
 * Polymorphic subject types a `ProjectComment` row can be attached to.
 *
 * - `DELIVERABLE`: a legacy `OutputRequirement` id (free-form string).
 * - `ARTIFACT`: a `ProjectArtifact` UUID.
 * - `FILE`: the project-relative GCS path of any project data file
 *   (e.g. `source-data/roster.csv`, `deliverables/decks/board-v1.pptx`).
 */
export type ProjectCommentSubjectType = 'DELIVERABLE' | 'ARTIFACT' | 'FILE';

/** Wire shape returned by the polymorphic comments API. Mirrors `ProjectCommentView`. */
export interface ProjectCommentView {
  id: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  subjectType: ProjectCommentSubjectType;
  subjectId: string;
  userId: string;
  userName: string;
  content: string;
  parentCommentId: string | null;
  isResolved: boolean;
  replies: ProjectCommentView[];
}

/**
 * Fetches every comment attached to a single subject. The {@link subjectId}
 * is URL-encoded so file paths with slashes survive routing — Express +
 * NestJS decode the segment back to the original string before the handler
 * runs.
 */
export async function listSubjectComments(
  projectId: string,
  subjectType: ProjectCommentSubjectType,
  subjectId: string,
): Promise<ProjectCommentView[]> {
  const response = await apiAxios.get<ProjectCommentView[]>(
    `/managed-projects/${projectId}/comments/${subjectType}/${encodeURIComponent(subjectId)}`,
  );
  return response.data;
}

export async function createSubjectComment(
  projectId: string,
  body: {
    subjectType: ProjectCommentSubjectType;
    subjectId: string;
    content: string;
    parentCommentId?: string;
  },
): Promise<ProjectCommentView> {
  const response = await apiAxios.post<ProjectCommentView>(`/managed-projects/${projectId}/project-comments`, body);
  return response.data;
}

export async function updateSubjectComment(
  projectId: string,
  commentId: string,
  body: { content?: string; isResolved?: boolean },
): Promise<ProjectCommentView> {
  const response = await apiAxios.patch<ProjectCommentView>(
    `/managed-projects/${projectId}/project-comments/${commentId}`,
    body,
  );
  return response.data;
}

export async function deleteSubjectComment(projectId: string, commentId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/project-comments/${commentId}`);
}

/** @deprecated Mirror of {@link ProjectCommentView}. New code should use the polymorphic helpers above. */
export interface DeliverableComment {
  id: string;
  createdAt: string;
  updatedAt: string;
  deliverableId: string;
  userId: string;
  userName: string;
  content: string;
  parentCommentId: string | null;
  isResolved: boolean;
  replies: DeliverableComment[];
}

export async function listDeliverableComments(projectId: string, deliverableId: string): Promise<DeliverableComment[]> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/deliverables/${deliverableId}/comments`);
  return response.data;
}

export async function listAllProjectComments(projectId: string): Promise<Record<string, DeliverableComment[]>> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/comments`);
  return response.data;
}

export async function createDeliverableComment(
  projectId: string,
  deliverableId: string,
  data: { content: string; parentCommentId?: string },
): Promise<DeliverableComment> {
  const response = await apiAxios.post(`/managed-projects/${projectId}/deliverables/${deliverableId}/comments`, data);
  return response.data;
}

export async function updateDeliverableComment(
  projectId: string,
  deliverableId: string,
  commentId: string,
  data: { content?: string; isResolved?: boolean },
): Promise<DeliverableComment> {
  const response = await apiAxios.patch(
    `/managed-projects/${projectId}/deliverables/${deliverableId}/comments/${commentId}`,
    data,
  );
  return response.data;
}

export async function deleteDeliverableComment(
  projectId: string,
  deliverableId: string,
  commentId: string,
): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/deliverables/${deliverableId}/comments/${commentId}`);
}

/**
 * Saves edited text content to an existing GCS project data file.
 *
 * @param projectId - Managed project identifier
 * @param path - GCS-relative file path within the project's data folder
 * @param content - Edited UTF-8 content string
 * @param contentType - Optional MIME type override
 * @returns Save result with the stored path, byte count, and optional archive path
 */
export async function saveProjectDataFile(
  projectId: string,
  path: string,
  content: string,
  contentType?: string,
): Promise<{ path: string; bytes: number; archivedPath?: string }> {
  const response = await apiAxios.post<{ path: string; bytes: number; archivedPath?: string }>(
    `/managed-projects/${projectId}/data-files/save`,
    { path, content, encoding: 'utf8', ...(contentType ? { contentType } : {}) },
  );
  return response.data;
}

/** Result of {@link deleteProjectDataFile} — counts let the UI reason about side effects. */
export interface DeleteProjectDataFileResult {
  /** The project-relative path that was deleted. */
  path: string;
  /** How many `ProjectArtifactVersion` rows were cleaned up because they pointed at the deleted blob. */
  artifactVersionsRemoved: number;
  /** How many `ProjectArtifact` rows were deleted because their last version was wiped. */
  artifactsRemoved: number;
}

/**
 * Deletes a single GCS-backed file from a project workspace. Used for
 * agent-generated outputs and rendered decks that have no Prisma `File`
 * row (and therefore can't be reached via `DELETE /knowledge/files/:id`).
 *
 * The path is sent as a query parameter rather than a route segment so
 * GCS-relative paths with slashes don't collide with NestJS route
 * matching. The server cleans up matching `ProjectArtifact` versions in
 * the same transaction so the file tree, artifact panel and GCS bucket
 * stay in sync after the delete completes.
 *
 * @param projectId - Managed project identifier.
 * @param path - GCS-relative file path within the project's data folder.
 */
export async function deleteProjectDataFile(projectId: string, path: string): Promise<DeleteProjectDataFileResult> {
  const response = await apiAxios.delete<DeleteProjectDataFileResult>(`/managed-projects/${projectId}/data-files`, {
    params: { path },
  });
  return response.data;
}

// ── Project Artifacts ──────────────────────────────────────────────

/**
 * `INTERACTIVE_DOC` is the agent-authored, vibe-coded artifact type. The
 * underlying source is TSX validated against the
 * `@systemzero-inc/ui/agent-doc-kit` allowlist, compiled server-side
 * via esbuild and rendered into a sandboxed iframe by `AgentDocViewer`.
 *
 * Distinct from `DOCUMENT` (markdown / MDX deliverables) and `DASHBOARD`
 * (in-chat dashboards rendered into the React tree).
 */
export type ProjectArtifactType = 'DOCUMENT' | 'SPREADSHEET' | 'DASHBOARD' | 'CODE' | 'INTERACTIVE_DOC' | 'OTHER';
export type ProjectArtifactStatus = 'DRAFT' | 'IN_REVIEW' | 'PUBLISHED' | 'ARCHIVED';

export interface ProjectArtifactVersion {
  id: string;
  artifactId: string;
  versionNumber: number;
  gcsPath: string | null;
  contentHash: string | null;
  sizeBytes: number | null;
  contentType: string | null;
  note: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export interface ProjectArtifactProvenance {
  id: string;
  artifactId: string;
  traceId: string | null;
  spanId: string | null;
  threadId: string | null;
  sourceFileIds: string[];
  createdAt: string;
}

export interface ProjectArtifact {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  type: ProjectArtifactType;
  status: ProjectArtifactStatus;
  isPinned: boolean;
  legacyId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  versionCount: number;
  currentVersion: ProjectArtifactVersion | null;
  versions?: ProjectArtifactVersion[];
  provenance?: ProjectArtifactProvenance[];
}

export interface CreateProjectArtifactInput {
  title: string;
  description?: string;
  type: ProjectArtifactType;
  isPinned?: boolean;
  gcsPath?: string;
  contentType?: string;
  sizeBytes?: number;
  note?: string;
}

export interface UpdateProjectArtifactInput {
  title?: string;
  description?: string | null;
  status?: ProjectArtifactStatus;
  isPinned?: boolean;
}

export async function listProjectArtifacts(projectId: string): Promise<{ artifacts: ProjectArtifact[] }> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/artifacts`);
  return response.data;
}

export async function getProjectArtifact(projectId: string, artifactId: string): Promise<ProjectArtifact> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/artifacts/${artifactId}`);
  return response.data;
}

export async function createProjectArtifact(
  projectId: string,
  data: CreateProjectArtifactInput,
): Promise<ProjectArtifact> {
  const response = await apiAxios.post(`/managed-projects/${projectId}/artifacts`, data);
  return response.data;
}

export async function updateProjectArtifact(
  projectId: string,
  artifactId: string,
  data: UpdateProjectArtifactInput,
): Promise<ProjectArtifact> {
  const response = await apiAxios.patch(`/managed-projects/${projectId}/artifacts/${artifactId}`, data);
  return response.data;
}

export async function deleteProjectArtifact(projectId: string, artifactId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/artifacts/${artifactId}`);
}

/**
 * Fetches the iframe-ready HTML page for an `INTERACTIVE_DOC` artifact.
 *
 * The returned string is the full document body the viewer should set
 * as `iframe.srcdoc`. The api authenticates the caller and inlines the
 * compiled bundle into a self-contained HTML page; the iframe must
 * still be sandboxed (`allow-scripts` only — no `allow-same-origin`)
 * by the consumer to enforce the security boundary.
 *
 * @param projectId - The managed project id.
 * @param artifactId - Artifact id (must be of type `INTERACTIVE_DOC`).
 * @param versionNumber - Optional pinned version. Latest when omitted.
 */
export async function getInteractiveDocHtml(
  projectId: string,
  artifactId: string,
  versionNumber?: number,
): Promise<string> {
  const response = await apiAxios.get<string>(`/managed-projects/${projectId}/artifacts/${artifactId}/render`, {
    params: versionNumber ? { v: versionNumber } : undefined,
    responseType: 'text',
    transformResponse: [(data: unknown) => (typeof data === 'string' ? data : String(data))],
    headers: { Accept: 'text/html' },
  });
  return response.data;
}

// ── Project Rules ──────────────────────────────────────────────────

export interface ProjectRule {
  id: string;
  projectId: string;
  title: string;
  content: string;
  globs: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRuleInput {
  title: string;
  content: string;
  globs?: string[];
  isActive?: boolean;
}

export interface UpdateProjectRuleInput {
  title?: string;
  content?: string;
  globs?: string[];
  isActive?: boolean;
}

export async function listProjectRules(projectId: string): Promise<{ rules: ProjectRule[] }> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/rules`);
  return response.data;
}

export async function createProjectRule(projectId: string, data: CreateProjectRuleInput): Promise<ProjectRule> {
  const response = await apiAxios.post(`/managed-projects/${projectId}/rules`, data);
  return response.data;
}

export async function updateProjectRule(
  projectId: string,
  ruleId: string,
  data: UpdateProjectRuleInput,
): Promise<ProjectRule> {
  const response = await apiAxios.patch(`/managed-projects/${projectId}/rules/${ruleId}`, data);
  return response.data;
}

export async function deleteProjectRule(projectId: string, ruleId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/rules/${ruleId}`);
}

// ── Project Memories ───────────────────────────────────────────────

export type ProjectMemoryStatus = 'PROPOSED' | 'ACCEPTED' | 'ARCHIVED';

export interface ProjectMemory {
  id: string;
  projectId: string;
  content: string;
  status: ProjectMemoryStatus;
  confidence: number;
  traceId: string | null;
  spanId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProjectMemoryInput {
  content?: string;
  status?: ProjectMemoryStatus;
  confidence?: number;
}

export async function listProjectMemories(
  projectId: string,
  status?: ProjectMemoryStatus,
): Promise<{ memories: ProjectMemory[] }> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/memories`, {
    params: status ? { status } : undefined,
  });
  return response.data;
}

export async function updateProjectMemory(
  projectId: string,
  memoryId: string,
  data: UpdateProjectMemoryInput,
): Promise<ProjectMemory> {
  const response = await apiAxios.patch(`/managed-projects/${projectId}/memories/${memoryId}`, data);
  return response.data;
}

export async function deleteProjectMemory(projectId: string, memoryId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/memories/${memoryId}`);
}

// ── Project Docs ───────────────────────────────────────────────────

export interface ProjectDoc {
  id: string;
  projectId: string;
  url: string;
  title: string | null;
  indexedAt: string | null;
  chunkCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectDocInput {
  url: string;
  title?: string;
}

export interface UpdateProjectDocInput {
  title?: string;
  isActive?: boolean;
}

export async function listProjectDocs(projectId: string): Promise<{ docs: ProjectDoc[] }> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/docs`);
  return response.data;
}

export async function createProjectDoc(projectId: string, data: CreateProjectDocInput): Promise<ProjectDoc> {
  const response = await apiAxios.post(`/managed-projects/${projectId}/docs`, data);
  return response.data;
}

export async function updateProjectDoc(
  projectId: string,
  docId: string,
  data: UpdateProjectDocInput,
): Promise<ProjectDoc> {
  const response = await apiAxios.patch(`/managed-projects/${projectId}/docs/${docId}`, data);
  return response.data;
}

export async function deleteProjectDoc(projectId: string, docId: string): Promise<void> {
  await apiAxios.delete(`/managed-projects/${projectId}/docs/${docId}`);
}

// ── Project Events (unified feed) ────────────────────────────────────────────

/** Unified project event type returned by the /events endpoint. */
export type ProjectEventType =
  | 'ACTIVITY'
  | 'ARTIFACT_CREATED'
  | 'ARTIFACT_UPDATED'
  | 'ARTIFACT_PUBLISHED'
  | 'RULE_ADDED'
  | 'RULE_UPDATED'
  | 'MEMORY_PROPOSED'
  | 'MEMORY_ACCEPTED'
  | 'DOC_ADDED'
  | 'FILE_UPLOAD'
  | 'CHAT_TURN';

/** Single event in the unified project event feed. */
export interface ProjectEvent {
  id: string;
  eventType: ProjectEventType;
  title: string;
  description?: string | null;
  status?: string | null;
  actorLabel?: string | null;
  timestamp: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * Fetches the unified project event feed (artifacts, rules, memories, docs,
 * activity jobs) sorted newest-first.
 */
export async function listProjectEvents(projectId: string, limit = 50): Promise<{ events: ProjectEvent[] }> {
  const response = await apiAxios.get(`/managed-projects/${projectId}/events`, {
    params: { limit },
  });
  return response.data;
}
